document.addEventListener('DOMContentLoaded', () => {
  if (window.__headerCountdownInitialized) return;
  window.__headerCountdownInitialized = true;

  let countdownEntries = [];

  const localeLabels = {
    da: ['timer', 'min', 'sek'],
    en: ['hours', 'min', 'sec'],
    de: ['std', 'min', 'sek']
  };

  const documentLang = (document.documentElement.getAttribute('lang') || 'en').toLowerCase();
  const languageKey = documentLang.split('-')[0];
  const labels = localeLabels[languageKey] || localeLabels.en;

  const parseTarget = (value) => {
    if (!value) return null;

    const normalised = value.trim().replace(' ', 'T');
    const match = normalised.match(/^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2}))?)?$/);
    if (!match) return null;

    const [, year, month, day, hour = '0', minute = '0', second = '0'] = match;
    const date = new Date(
      parseInt(year, 10),
      parseInt(month, 10) - 1,
      parseInt(day, 10),
      parseInt(hour, 10),
      parseInt(minute, 10),
      parseInt(second, 10)
    );

    return {
      date,
      hasSeconds: Boolean(match[6])
    };
  };

  const parseCutoff = (value) => {
    if (!value) return null;

    const normalised = value.trim();
    const match = normalised.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (!match) return null;

    const [hours, minutes, seconds = '0'] = match.slice(1).map((part) => parseInt(part, 10));

    if (
      [hours, minutes, seconds].some((unit) => Number.isNaN(unit)) ||
      hours < 0 || hours > 23 ||
      minutes < 0 || minutes > 59 ||
      seconds < 0 || seconds > 59
    ) {
      return null;
    }

    return {
      value: { hours, minutes, seconds },
      hasSeconds: Boolean(match[3])
    };
  };

  const getNextBusinessCutoff = (reference, cutoff) => {
    const target = new Date(
      reference.getFullYear(),
      reference.getMonth(),
      reference.getDate(),
      cutoff.hours,
      cutoff.minutes,
      cutoff.seconds,
      0
    );

    let next = new Date(target.getTime());

    do {
      next.setDate(next.getDate() + 1);
    } while (next.getDay() === 0 || next.getDay() === 6);

    next.setHours(cutoff.hours, cutoff.minutes, cutoff.seconds, 0);
    return next;
  };

  const getCutoffState = (reference, cutoff) => {
    if (!cutoff) return null;

    const isWeekend = [0, 6].includes(reference.getDay?.() ?? -1);
    if (isWeekend) {
      return {
        active: false,
        nextTarget: getNextBusinessCutoff(reference, cutoff)
      };
    }

    const todayTarget = new Date(
      reference.getFullYear(),
      reference.getMonth(),
      reference.getDate(),
      cutoff.hours,
      cutoff.minutes,
      cutoff.seconds,
      0
    );

    if (reference < todayTarget) {
      return { active: true, target: todayTarget };
    }

    return {
      active: false,
      nextTarget: getNextBusinessCutoff(reference, cutoff)
    };
  };

  const pad = (value) => value.toString().padStart(2, '0');

  const formatDuration = (diff, entry) => {
    const totalSeconds = Math.floor(diff / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const segments = [];

    if (hours > 0) {
      segments.push(`${pad(hours)} ${labels[0]}`);
    }

    segments.push(`${pad(minutes)} ${labels[1]}`);

    if (entry.hasSeconds) {
      segments.push(`${pad(seconds)} ${labels[2]}`);
    }

    return segments.join(' ');
  };

  const evaluateCountdown = (entry, now) => {
    if (entry.mode === 'date') {
      const diff = entry.target.getTime() - now.getTime();
      return diff > 0 ? formatDuration(diff, entry) : entry.defaultText;
    }

    if (entry.mode === 'cutoff') {
      const state = getCutoffState(now, entry.cutoff);

      if (state?.active) {
        const remaining = state.target.getTime() - now.getTime();
        return formatDuration(remaining, entry);
      }

      return entry.defaultText;
    }

    return entry.defaultText;
  };

  const collectCountdowns = () => {
    const containers = Array.from(document.querySelectorAll('[data-countdown-target]'));

    return containers.reduce((acc, container) => {
      const timerNode = container.querySelector('.header__countdown-timer');
      if (!timerNode) return acc;

      const defaultText = timerNode.getAttribute('data-countdown-default') || '';
      const targetResult = parseTarget(container.getAttribute('data-countdown-target'));
      const cutoffResult = parseCutoff(container.getAttribute('data-countdown-cutoff'));
      const targetDate = targetResult ? targetResult.date : null;
      const cutoffValue = cutoffResult ? cutoffResult.value : null;
      const labelNode = container.querySelector('[data-countdown-label]');
      const postNode = container.querySelector('[data-countdown-post]');

      const countdown = {
        container,
        node: timerNode,
        label: labelNode,
        post: postNode,
        defaultText,
        target: targetDate,
        cutoff: cutoffValue,
        hasSeconds: Boolean(targetResult?.hasSeconds || cutoffResult?.hasSeconds),
        mode: cutoffValue ? 'cutoff' : targetDate instanceof Date && !Number.isNaN(targetDate.getTime()) ? 'date' : 'default'
      };

      acc.push(countdown);
      return acc;
    }, []);
  };

  const updateCountdownVisibility = () => {
    const isMobile = window.matchMedia('(max-width: 999px)').matches;
    const shouldHide = isMobile && window.scrollY > 0;

    countdownEntries.forEach(({ container }) => {
      if (!container.classList.contains('is-active')) {
        container.removeAttribute('hidden');
        return;
      }

      if (shouldHide) {
        container.setAttribute('hidden', 'hidden');
      } else {
        container.removeAttribute('hidden');
      }
    });
  };

  const init = () => {
    const countdowns = collectCountdowns();
    countdownEntries = countdowns;

    if (!countdowns.length) {
      updateCountdownVisibility();
      return;
    }

    const updateCountdowns = () => {
      const now = new Date();
      countdowns.forEach((entry) => {
        const result = evaluateCountdown(entry, now);
        entry.node.textContent = result;
        const isDefault = result === entry.defaultText;
        entry.node.classList.toggle('is-default', isDefault);

        if (entry.label) {
          if (isDefault) {
            entry.label.setAttribute('hidden', 'hidden');
          } else {
            entry.label.removeAttribute('hidden');
          }
        }

        if (entry.post) {
          if (isDefault) {
            entry.post.setAttribute('hidden', 'hidden');
          } else {
            entry.post.removeAttribute('hidden');
          }
        }
      });
    };

    updateCountdowns();
    countdowns.forEach((entry) => {
      entry.container.classList.remove('is-pending');
      entry.container.classList.add('is-active');
      entry.container.removeAttribute('hidden');

      if (entry.label && entry.node.classList.contains('is-default')) {
        entry.label.setAttribute('hidden', 'hidden');
      }

      if (entry.post && entry.node.classList.contains('is-default')) {
        entry.post.setAttribute('hidden', 'hidden');
      }
    });

    if (window.__headerCountdownInterval) {
      window.clearInterval(window.__headerCountdownInterval);
    }

    window.__headerCountdownInterval = window.setInterval(updateCountdowns, 1000);

    document.addEventListener('shopify:section-unload', (event) => {
      if (!event?.detail) return;

      const section = document.getElementById(`shopify-section-${event.detail.sectionId}`);

      if (section && !section.querySelector('[data-countdown-target]')) {
        window.clearInterval(window.__headerCountdownInterval);
        window.__headerCountdownInterval = null;
      }
    });

    updateCountdownVisibility();
  };

  if (!window.__headerCountdownScrollListenerAttached) {
    window.addEventListener('scroll', updateCountdownVisibility, { passive: true });
    window.__headerCountdownScrollListenerAttached = true;
  }

  init();
  document.addEventListener('shopify:section:load', init);
});